import { useEffect, useRef, useState } from 'react';
import { scrollToDocsSection } from './nav';
import { getMermaid } from '../../lib/mermaid';

/**
 * Renders a Mermaid graph, themed to the Dispatch Desk palette, with optional
 * clickable nodes that jump to a docs section. The Mermaid loader/theme is
 * shared with the generic markdown MermaidBlock (see lib/mermaid.ts).
 *
 * The chart text is static and authored in this repo (never user input), so the
 * generated SVG is injected via dangerouslySetInnerHTML.
 */

let renderSeq = 0;

export function MermaidDiagram({
  chart,
  links,
  caption,
  ariaLabel,
  className = '',
}: {
  chart: string;
  /** Map of Mermaid node key → docs section id. Makes those nodes clickable. */
  links?: Record<string, string>;
  caption?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getMermaid()
      .then(async (mermaid) => {
        renderSeq += 1;
        const { svg: rendered } = await mermaid.render(`mmd-${renderSeq}`, chart);
        if (!cancelled) setSvg(rendered);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [chart]);

  // Bind click + affordance to the nodes named in `links`, once the SVG exists.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !svg || !links) return;
    const keys = Object.keys(links);
    const cleanups: Array<() => void> = [];
    root.querySelectorAll<SVGGElement>('g.node').forEach((node) => {
      // Mermaid node group ids look like `flowchart-<key>-<n>`; match on a
      // word-boundary so `API` doesn't also match `APIServer`.
      const key = keys.find((k) =>
        new RegExp(`(^|[^A-Za-z0-9])${k}([^A-Za-z0-9]|$)`).test(node.id),
      );
      if (!key) return;
      const section = links[key];
      node.style.cursor = 'pointer';
      node.classList.add('mermaid-node-link');
      const onClick = () => scrollToDocsSection(section);
      node.addEventListener('click', onClick);
      cleanups.push(() => node.removeEventListener('click', onClick));
    });
    return () => cleanups.forEach((fn) => fn());
  }, [svg, links]);

  // Graceful fallback: show the source if Mermaid fails to load/parse.
  if (failed) {
    return (
      <pre className="mb-4 overflow-x-auto rounded-lg border border-line bg-surface-2 px-4 py-3 font-mono text-xs leading-relaxed text-ink-muted">
        {chart}
      </pre>
    );
  }

  return (
    <figure className={`mb-4 ${className}`}>
      <div
        ref={containerRef}
        role="img"
        aria-label={ariaLabel}
        className="mermaid-diagram flex justify-center overflow-x-auto rounded-lg border border-line bg-surface-1 px-4 py-5"
        // svg is produced by Mermaid from a static, app-authored chart string
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {caption && (
        <figcaption className="mt-2 text-center text-xs text-ink-faint">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
